// SYNC: mirrors select-web-client-patch/src/components/common/CookieConsent/CookieConsentSheet.tsx
import React, { useEffect, useRef } from "react";
import { privacyPolicyLink } from "../../Constants";
import { toI18N } from "../../i18n";
import CookieMark from "./CookieMark";

interface Props {
	open: boolean;
	onAcceptAll: () => void;
	onEssentialOnly: () => void;
	onManageSettings: () => void;
}

// First consent layer, rendered by us rather than by vanilla-cookieconsent so
// the approved design is reproducible across the two surfaces. The web client
// builds the same panel from its design system; this repo has no design system
// and no Tailwind 4 tokens, so the markup is hand-written against the local
// palette. Shape (weights, radii, sizes, spacing) is matched, colours are this
// repo's own.
//
// Not dismissible: the visitor has to choose, so there is no close button, no
// outside-click handler and no Escape handler.
const CookieConsentSheet = ({
	open,
	onAcceptAll,
	onEssentialOnly,
	onManageSettings,
}: Props) => {
	const panelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;

		// Without Radix there is no focus trap — a known, accepted cost. Moving
		// focus to the panel at least announces the dialog and puts the choice
		// next in the tab order.
		panelRef.current?.focus();

		// The scrim blocks pointer input; this stops the page scrolling behind
		// it, which is the rest of what `disablePageInteraction` used to do.
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [open]);

	if (!open) return null;

	return (
		<div className="fixed inset-0 z-50 flex flex-col justify-end">
			<div className="absolute inset-0 bg-black/50" aria-hidden="true" />

			<div
				ref={panelRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="cookie-consent-title"
				tabIndex={-1}
				className="relative w-full rounded-t-2xl bg-neutral-1 pb-6 shadow-[0_-12px_44px_rgb(0_0_0/0.28)] outline-none"
			>
				<div className="mx-auto mt-3 mb-0.5 h-1 w-10 rounded-full bg-neutral-7" />

				<div className="mx-auto w-full max-w-[52rem] px-7 pt-3.5 max-[620px]:px-5">
					<div className="flex flex-wrap items-center gap-5">
						<CookieMark />

						<div className="min-w-0 flex-1 basis-64">
							<h2
								id="cookie-consent-title"
								className="mb-1 text-base font-bold tracking-tight text-neutral-2"
							>
								{toI18N("cookie_banner_title")}
							</h2>
							<p className="mb-1 text-sm text-neutral-6">
								{toI18N("cookie_banner_description")}{" "}
								<a
									href={privacyPolicyLink}
									target="_blank"
									rel="noreferrer"
									className="underline underline-offset-2"
								>
									{toI18N("cookie_privacy_policy")}
								</a>
							</p>
							<button
								type="button"
								onClick={onManageSettings}
								className="cursor-pointer text-sm text-neutral-6 underline underline-offset-2 hover:text-neutral-2"
							>
								{toI18N("cookie_manage_preferences")}
							</button>
						</div>

						<div className="flex flex-shrink-0 items-center gap-2 max-[620px]:w-full">
							<button
								type="button"
								onClick={onEssentialOnly}
								className="h-9 min-w-[8rem] rounded-md border border-neutral-7 bg-neutral-1 px-4 text-sm font-semibold text-neutral-2 hover:bg-neutral-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-1 max-[620px]:min-w-0 max-[620px]:flex-1"
							>
								{toI18N("cookie_essential_only")}
							</button>
							<button
								type="button"
								onClick={onAcceptAll}
								className="h-9 min-w-[8rem] rounded-md bg-primary-1 px-4 text-sm font-semibold text-neutral-1 hover:bg-primary-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-1 max-[620px]:min-w-0 max-[620px]:flex-1"
							>
								{toI18N("cookie_accept_all")}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CookieConsentSheet;
